import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  message,
  Card,
  Divider,
  Checkbox,
} from 'antd';
import { MailOutlined, DeleteOutlined, EditOutlined, SendOutlined } from '@ant-design/icons';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const SubscriberManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [emailVisible, setEmailVisible] = useState(false);
  const [bulkEmailVisible, setBulkEmailVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [form] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [bulkEmailContent, setBulkEmailContent] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedEmailVisible, setSelectedEmailVisible] = useState(false);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/subscribers');
      setSubscribers(data);
    } catch (err) {
      setErrorMsg('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      email: record.email,
    });
    setEditingId(record.id);
    setVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete this subscriber?',
      content: 'This action cannot be undone.',
      onOk: async () => {
        try {
          await api.delete(`/subscribers/${id}`);
          fetchSubscribers();
          message.success('Subscriber deleted successfully');
        } catch {
          message.error('Failed to delete subscriber');
        }
      },
    });
  };

  const handleSendEmail = (subscriber) => {
    setSelectedSubscriber(subscriber);
    emailForm.resetFields();
    setEmailContent('');
    setEmailVisible(true);
  };

  const handleSendBulkEmail = () => {
    emailForm.resetFields();
    setBulkEmailContent('');
    setBulkEmailVisible(true);
  };

  const handleSendSelectedEmail = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one subscriber');
      return;
    }
    emailForm.resetFields();
    setEmailContent('');
    setSelectedEmailVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingId) {
        await api.put(`/subscribers/${editingId}`, values);
        setSuccessMsg('Subscriber updated successfully');
      } else {
        await api.post('/subscribers', values);
        setSuccessMsg('Subscriber added successfully');
      }
      
      setVisible(false);
      fetchSubscribers();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleEmailSubmit = async () => {
    try {
      const values = await emailForm.validateFields();
      
      if (selectedSubscriber) {
        await api.post(`/subscribers/send-email/${selectedSubscriber.id}`, {
          subject: values.subject,
          message: emailContent,
        });
        message.success('Email sent successfully');
      }
      
      setEmailVisible(false);
    } catch (err) {
      message.error('Failed to send email');
    }
  };

  const handleBulkEmailSubmit = async () => {
    try {
      const values = await emailForm.validateFields();
      
      await api.post('/subscribers/send-bulk-email', {
        subject: values.subject,
        message: bulkEmailContent,
      });
      
      message.success(`Email sent to ${subscribers.length} subscribers`);
      setBulkEmailVisible(false);
    } catch (err) {
      message.error('Failed to send bulk email');
    }
  };

  const handleSelectedEmailSubmit = async () => {
    try {
      const values = await emailForm.validateFields();
      
      await api.post('/subscribers/send-multiple-emails', {
        subscriberIds: selectedRowKeys,
        subject: values.subject,
        message: emailContent,
      });
      
      message.success(`Email sent to ${selectedRowKeys.length} subscribers`);
      setSelectedRowKeys([]);
      setSelectedEmailVisible(false);
    } catch (err) {
      message.error('Failed to send email to selected subscribers');
    }
  };

  const onSelectChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const hasSelected = selectedRowKeys.length > 0;

  const columns = [
    {
  title: 'S.No.',
  key: 'sno',
  width: 80,
  render: (_text, _record, index) => index + 1,
},
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email' 
    },
    { 
      title: 'Subscribed At', 
      dataIndex: 'subscribed_at', 
      key: 'subscribed_at',
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button 
            icon={<MailOutlined />} 
            onClick={() => handleSendEmail(record)}
          >
            Send Email
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2>Subscriber Management</h2>

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAdd}>Add Subscriber</Button>
        <Button 
          type="primary" 
          icon={<SendOutlined />} 
          onClick={handleSendSelectedEmail}
          disabled={!hasSelected}
        >
          Send to Selected ({selectedRowKeys.length})
        </Button>
      </Space>

      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          closable
          onClose={() => setSuccessMsg('')}
          style={{ marginBottom: 12 }}
        />
      )}

      {errorMsg && (
        <Alert
          message={errorMsg}
          type="error"
          showIcon
          closable
          onClose={() => setErrorMsg('')}
          style={{ marginBottom: 12 }}
        />
      )}

      <Card title={`Subscribers (${subscribers.length})`}>
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginLeft: 8 }}>
            {hasSelected ? `Selected ${selectedRowKeys.length} items` : ''}
          </span>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={subscribers}
          loading={loading}
          pagination={{ pageSize: 10 }}
          rowSelection={rowSelection}
        />
      </Card>

      {/* Add/Edit Subscriber Modal */}
      <Modal
        title={editingId ? 'Edit Subscriber' : 'Add Subscriber'}
        visible={visible}
        onOk={handleSubmit}
        onCancel={() => setVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        title={`Send Email to ${selectedSubscriber?.email || ''}`}
        visible={emailVisible}
        onOk={handleEmailSubmit}
        onCancel={() => setEmailVisible(false)}
        width={800}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please input subject!' }]}
          >
            <Input />
          </Form.Item>
          <Divider />
          <h4>Email Content</h4>
          <ReactQuill 
            theme="snow" 
            value={emailContent} 
            onChange={setEmailContent} 
            style={{ height: '300px', marginBottom: '40px' }}
          />
        </Form>
      </Modal>

      {/* Send Bulk Email Modal */}
      <Modal
        title={`Send Email to All Subscribers (${subscribers.length})`}
        visible={bulkEmailVisible}
        onOk={handleBulkEmailSubmit}
        onCancel={() => setBulkEmailVisible(false)}
        width={800}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please input subject!' }]}
          >
            <Input />
          </Form.Item>
          <Divider />
          <h4>Email Content</h4>
          <ReactQuill 
            theme="snow" 
            value={bulkEmailContent} 
            onChange={setBulkEmailContent} 
            style={{ height: '300px', marginBottom: '40px' }}
          />
        </Form>
      </Modal>

      {/* Send Email to Selected Subscribers Modal */}
      <Modal
        title={`Send Email to Selected Subscribers (${selectedRowKeys.length})`}
        visible={selectedEmailVisible}
        onOk={handleSelectedEmailSubmit}
        onCancel={() => setSelectedEmailVisible(false)}
        width={800}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please input subject!' }]}
          >
            <Input />
          </Form.Item>
          <Divider />
          <h4>Email Content</h4>
          <ReactQuill 
            theme="snow" 
            value={emailContent} 
            onChange={setEmailContent} 
            style={{ height: '300px', marginBottom: '40px' }}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriberManagement;